import React, { useEffect, useRef, useState } from 'react';
import { Container, Section } from 'components/shared';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useDeviceWidth from 'utils/hooks/useDeviceWidth';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import cx from 'classnames';
import breakpoints from 'utils/breakpoints';
import { useCountryValue } from 'stores/country';
import { isValidTitle } from 'utils/validateTitle';
import { StyledModal } from 'components/shared';
import CloseIcon from 'components/icons/Close';

import { Copy } from 'components/components/atoms';
import { PromoCard } from 'components/components/molecules';

const ModalContent = styled.div`
  ${({ isMobile }) =>
    isMobile
      ? `
        width: 91.5vw;
        height: 90vh;
        margin: auto;
      `
      : `
        width: 80vw;
        height: 80vh;
        margin: auto;
        max-width: 69.625rem;
      `}
`;

const CarouselContainer = styled.div`
  & .slick-list {
    overflow: visible !important;
  }
  & .slick-track {
    display: flex;
  }
`;

const PromoGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;

  @media ${breakpoints.md} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${breakpoints['2xl']} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  & > div > a {
    width: 100%;
  }

  & > div > a > div {
    min-width: 0;
    width: 100%;
  }
`;

const Heading = styled.h2`
  max-width: 32.063rem;
  letter-spacing: -0.5px;
  line-height: 2.625rem;
`;

const StyledCopy = styled.p`
  max-width: 39.938rem;
  letter-spacing: 0.11px;
`;

const getPromoCards = (cards, country, findSize, handleShowModal) => {
  return cards
    .filter((c) => c.country === country)
    .map((c, i) => (
      <div key={c.title + c.subtitle} className='md:pb-16 md:pt-4'>
        <PromoCard
          style={findSize ? { scale: '.65' } : { scale: '1' }}
          promoCard={c}
          data-aos='fade-left'
          data-aos-delay={i * 150}
          handleShowModal={handleShowModal}
        />
      </div>
    ));
};

const getSlides = (
  cards,
  country,
  findSize,
  handleShowModal,
  isMobile,
  mobileCardWidth,
) => {
  return cards
    .filter((c) => c.country === country)
    .map((c, i) => (
      <div
        key={c.title + c.subtitle}
        className='p-2 md:pb-16 md:pt-4'
        style={isMobile ? { width: mobileCardWidth } : undefined}
      >
        <PromoCard
          style={findSize ? { scale: '.65' } : { scale: '1' }}
          promoCard={c}
          data-aos='fade-left'
          data-aos-delay={i * 150}
          handleShowModal={handleShowModal}
        />
      </div>
    ));
};

const PromoBlock = ({ fields, rendering }) => {
  const cardsViewModel = getFieldValue(fields, 'cardsViewModel') || [];
  const title = getFieldValue(fields, 'title');
  const description = getFieldValue(fields, 'description');
  const [showModalDisclaimer, setShowModalDisclaimer] = useState(false);
  const [promoDisclaimerText, setPromoDisclaimerText] = useState();

  const { componentName } = rendering;
  const [{ country }] = useCountryValue();
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const deviceWidth = useDeviceWidth();
  const mobileCardWidth = deviceWidth && Math.round(deviceWidth * 0.85);
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);

  const [scaleDownPromoCards, setScaleDownPromoCards] = useState(false);
  const targetRefFontSize = useRef();

  const handleShowModal = (text) => {
    setShowModalDisclaimer(true);
    setPromoDisclaimerText(text);
  };

  const [slides, setSlides] = useState(
    getSlides(
      cardsViewModel,
      country,
      false,
      handleShowModal,
      isMobile,
      mobileCardWidth,
    ),
  );

  useEffect(() => {
    const observer = new ResizeObserver(checkFontSizeChange);
    if (targetRefFontSize && targetRefFontSize.current) {
      observer.observe(targetRefFontSize.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setSlides(
      getSlides(
        cardsViewModel,
        country,
        false,
        handleShowModal,
        isMobile,
        mobileCardWidth,
      ),
    );
  }, [isMobile, mobileCardWidth, country, componentName]);

  const checkFontSizeChange = () => {
    if (targetRefFontSize.current) {
      const fontSize = parseInt(
        getComputedStyle(targetRefFontSize.current).fontSize,
        10,
      );

      const findSize = fontSize === 32;
      setScaleDownPromoCards(findSize);
      setSlides(
        getSlides(
          cardsViewModel,
          country,
          findSize,
          handleShowModal,
          isMobile,
          mobileCardWidth,
        ),
      );
    }
  };

  if (componentName === 'PromoBlock' && slides.length < 1) return null;

  return (
    <>
      <Section ref={targetRefFontSize}>
        <Container className='md:py-8'>
          {isValidTitle(title) && (
            <Heading
              className='font-bold font-trade-gothic-20 mb-1 md:mb-2 text-4xl text-mattamy-blue'
              data-aos='fade-down'
            >
              {title}
            </Heading>
          )}
          <StyledCopy
            className='text-normal mb-4 md:mb-2 font-graphie text-mattamy-blue'
            data-aos='fade-down'
            data-aos-delay='100'
            data-aos-offset='100'
          >
            {description}
          </StyledCopy>
        </Container>

        <Container>
          <PromoGrid>
            {getPromoCards(
              cardsViewModel,
              country,
              scaleDownPromoCards,
              handleShowModal,
            )}
          </PromoGrid>
        </Container>
      </Section>

      <StyledModal
        isOpen={showModalDisclaimer}
        className='bg-black-faded-light select-none flex w-full'
        data-aos='fade'
        tabIndex={0}
      >
        <ModalContent
          isMobile={isMobile}
          className='overflow-auto h-full rounded content-center'
          aria-modal='true'
          role='dialog'
        >
          <div className='w-full flex flex-col bg-white rounded text-action-blue pt-10 px-5 md:px-10'>
            <button
              className='flex justify-end -mt-3 -mr-3 z-10 text-mattamy-blue'
              onClick={() => setShowModalDisclaimer(false)}
              aria-label={'Close disclaimer text modal'}
            >
              <CloseIcon className='w-8' />
            </button>

            <Copy className='text-left pt-1 md:pt-0 pb-24 md:pb-5 lg:pb-10 tracking-tight'>
              {promoDisclaimerText}
            </Copy>
          </div>
        </ModalContent>
      </StyledModal>
    </>
  );
};

export default PromoBlock;
